
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

export const usePatientManagement = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { session } = useAuth();

  const addPatient = useMutation({
    mutationFn: async (patientEmail: string) => {
      if (!session?.user?.id) throw new Error("Non authentifié");

      const trimmedEmail = patientEmail.trim().toLowerCase();
      console.log("Looking for patient with email:", trimmedEmail);

      const { data: patientProfiles, error: patientError } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, type')
        .ilike('email', trimmedEmail)
        .eq('type', 'patient')
        .maybeSingle();

      if (patientError) {
        console.error("Error fetching patient profile:", patientError);
        throw new Error("Erreur lors de la recherche du patient");
      }

      if (!patientProfiles) {
        console.log("No patient found with email:", trimmedEmail);
        throw new Error("Aucun patient trouvé avec cet email. Veuillez vérifier l'adresse email.");
      }

      // Vérifier si une relation existe déjà
      const { data: existingRelation, error: relationCheckError } = await supabase
        .from("professional_patients")
        .select("id, status")
        .eq("professional_id", session.user.id)
        .eq("patient_id", patientProfiles.id)
        .maybeSingle();

      if (relationCheckError) {
        console.error("Error checking existing relationship:", relationCheckError);
        throw new Error("Impossible de vérifier la relation existante");
      }

      if (existingRelation) {
        throw new Error(`Ce patient est déjà dans votre liste (statut: ${existingRelation.status})`);
      }

      // Obtenir les informations du professionnel
      const { data: professionalProfile, error: profileError } = await supabase
        .from("profiles")
        .select("full_name, company_name")
        .eq("id", session.user.id)
        .single();

      if (profileError) {
        console.error("Error fetching professional profile:", profileError);
      }

      // Créer la demande d'accès aux données du patient
      const { error: accessError } = await supabase
        .from("professional_patient_access")
        .insert({
          professional_id: session.user.id,
          patient_id: patientProfiles.id,
          status: 'pending',
          journal_access: true,
          medications_access: true,
          mood_access: true
        });

      if (accessError) {
        console.error("Error creating access request:", accessError);
        if (accessError.code === "23505") {
          throw new Error("Une demande d'accès existe déjà pour ce patient");
        } else {
          throw new Error("Impossible de créer la demande d'accès");
        }
      }

      // Créer la relation professionnel-patient
      const { error: relationError } = await supabase
        .from("professional_patients")
        .insert({
          patient_id: patientProfiles.id,
          professional_id: session.user.id,
          status: 'pending'
        });

      if (relationError) {
        console.error("Error creating relationship:", relationError);
        throw new Error("Impossible d'ajouter le patient");
      }

      // Créer une notification pour le patient avec plus d'informations
      const companyInfo = professionalProfile?.company_name 
        ? ` (${professionalProfile.company_name})` 
        : '';
      
      const professionalName = professionalProfile?.full_name || session.user.email;
      
      const { error: notificationError } = await supabase
        .from("notifications")
        .insert({
          user_id: patientProfiles.id,
          title: "Nouvelle demande d'accès",
          content: `Le professionnel de santé ${professionalName}${companyInfo} souhaite accéder à votre dossier médical. Veuillez accepter ou refuser la demande dans la section Journal.`,
          type: "access_request"
        });

      if (notificationError) {
        console.error("Error creating notification:", notificationError);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professional-patients'] });
      toast({
        title: "Succès",
        description: "Demande d'accès envoyée au patient",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updatePatientStatus = useMutation({
    mutationFn: async ({ patientId, status }: { patientId: string; status: 'approved' | 'rejected' }) => {
      if (!session?.user?.id) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("professional_patients")
        .update({ status })
        .eq("professional_id", session.user.id)
        .eq("patient_id", patientId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["professional-patients"] });
    },
  });

  const deletePatient = useMutation({
    mutationFn: async (patientId: string) => {
      if (!session?.user?.id) throw new Error("Non authentifié");

      const { error } = await supabase
        .from("professional_patients")
        .delete()
        .eq("professional_id", session.user.id)
        .eq("patient_id", patientId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["professional-patients"] });
    },
  });

  return {
    addPatient,
    updatePatientStatus,
    deletePatient,
  };
};
